import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Play, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { usePlayer } from "../context/PlayerContext";

// How many results the popup lists. "See all results" opens the full page.
const MAX_SHOWN = 6;

// Genre display names, fetched once, so typing "hip" can show "Rap / Hip-Hop".
let genreNamesPromise = null;
function loadGenreNames() {
  if (!genreNamesPromise) {
    genreNamesPromise = api.get("/genres")
      .then((r) => Object.fromEntries(r.data.map((g) => [g.slug, g.name])))
      .catch(() => { genreNamesPromise = null; return {}; });
  }
  return genreNamesPromise;
}

// If a song matched through its mood or genre (not its title or artist),
// say which one, so the result does not look random.
function whyMatched(v, q, genreNames) {
  const mood = (v.moods || []).find((m) => m.toLowerCase().includes(q));
  if (mood) return mood;
  const slug = (v.genres || []).find(
    (s) => s.toLowerCase().includes(q) || (genreNames[s] || "").toLowerCase().includes(q)
  );
  return slug ? (genreNames[slug] || slug) : null;
}

// Lower number = better match. Title matches first (starts-with, then a word
// starts with it, then anywhere), then artist, then mood/genre, then the rest.
function rank(v, q, genreNames) {
  const title = (v.title || "").toLowerCase();
  if (title.startsWith(q)) return 0;
  if (title.split(/[\s\-–—/(),.:!?'"]+/).some((w) => w.startsWith(q))) return 1;
  if (title.includes(q)) return 2;
  if ((v.artist || "").toLowerCase().includes(q)) return 3;
  if (whyMatched(v, q, genreNames)) return 4;
  return 5;
}

function Highlight({ text, query }) {
  const i = query ? text.toLowerCase().indexOf(query) : -1;
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-transparent text-cyan-300">{text.slice(i, i + query.length)}</mark>
      {text.slice(i + query.length)}
    </>
  );
}

// On a phone the on-screen keyboard can cover a bottom sheet. This measures how
// much of the screen the keyboard takes so the sheet can sit just above it.
function useKeyboardInset() {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => setInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);
  return inset;
}

// Layout idea: on a phone this is a sheet that rises from the bottom, with the
// search bar at the BOTTOM (where a thumb rests) and the best match right above
// it. On a wider screen it is a normal box near the top with the bar first.
function Sheet({ onClose }) {
  const nav = useNavigate();
  const player = usePlayer();
  const inset = useKeyboardInset();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState("");   // the text the shown results are for
  const [loading, setLoading] = useState(false);
  const [genreNames, setGenreNames] = useState({});
  const reqRef = useRef(0);

  useEffect(() => { let alive = true; loadGenreNames().then((m) => { if (alive) setGenreNames(m); }); return () => { alive = false; }; }, []);

  // Close with Esc, and stop the page behind from scrolling while the popup is open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  // Search as you type (waits a moment after the last keystroke).
  useEffect(() => {
    const text = q.trim();
    const my = ++reqRef.current;
    if (!text) { setResults([]); setSearched(""); setLoading(false); return undefined; }
    setLoading(true);
    const timer = setTimeout(() => {
      api.get(`/videos?q=${encodeURIComponent(text)}&limit=40`)
        .then((r) => { if (my === reqRef.current) { setResults(r.data); setSearched(text); } })
        .catch(() => { if (my === reqRef.current) { setResults([]); setSearched(text); } })
        .finally(() => { if (my === reqRef.current) setLoading(false); });
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const needle = searched.toLowerCase();
  const entries = useMemo(
    () => results
      .map((v) => {
        const r = rank(v, needle, genreNames);
        return { v, r, note: r >= 4 ? whyMatched(v, needle, genreNames) : null };
      })
      .sort((a, b) => a.r - b.r)
      .slice(0, MAX_SHOWN),
    [results, needle, genreNames]
  );
  const shown = entries.map((e) => e.v);

  const submit = (e) => {
    e.preventDefault();
    const text = q.trim();
    if (!text) return;
    onClose();
    nav(`/search?q=${encodeURIComponent(text)}`);
  };

  const play = (i) => { player.playQueue(shown, i); onClose(); };

  let status = null;
  if (!q.trim()) status = "Type part of a title, or an artist, mood or genre.";
  else if (loading && !entries.length) status = "Searching…";
  else if (searched && !entries.length) status = `No songs match “${searched}”. Try fewer letters.`;

  return (
    <div className="fixed left-0 right-0 top-0 z-[60] flex items-end sm:items-start sm:justify-center sm:pt-[10vh]"
      style={{ bottom: inset }} role="dialog" aria-modal="true" aria-label="Search songs" data-testid="search-popup">
      <button type="button" tabIndex={-1} aria-label="Close search" onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default" data-testid="search-popup-backdrop" />
      <div className="relative w-full sm:max-w-xl flex flex-col-reverse sm:flex-col max-h-[80dvh] sm:max-h-[75vh] bg-zinc-900 border-t sm:border border-white/10 rounded-t-3xl sm:rounded-2xl shadow-2xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>

        {/* Search bar: bottom of the sheet on a phone, top on a wide screen */}
        <form onSubmit={submit} className="flex items-center gap-2 p-3 border-t sm:border-t-0 sm:border-b border-white/10 shrink-0">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500 pointer-events-none" />
            <input autoFocus type="search" enterKeyHint="search" inputMode="search" autoComplete="off" autoCorrect="off"
              autoCapitalize="off" spellCheck={false} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Title, artist, mood or genre" aria-label="Search songs" data-testid="search-popup-input"
              className="w-full h-12 bg-white/5 border border-white/10 rounded-full pl-12 pr-4 text-base text-white placeholder:text-zinc-500 focus:outline-none focus:border-cyan-400/60" />
          </div>
          <button type="button" onClick={onClose} data-testid="search-popup-close"
            className="h-12 px-4 shrink-0 rounded-full text-sm font-medium text-zinc-300 hover:bg-white/10 active:bg-white/10">
            Close
          </button>
        </form>

        {/* Results: best match sits closest to the search bar. On a phone the list is
            built bottom-up (column-reverse), so it also starts scrolled to that end. */}
        <ul className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 flex flex-col-reverse sm:flex-col" data-testid="search-popup-results">
          {status && <li className="px-4 py-6 text-center text-sm text-zinc-400" data-testid="search-popup-status">{status}</li>}
          {entries.map(({ v, note }, i) => (
            <li key={v.id} className="flex items-center gap-1 rounded-xl hover:bg-white/5 active:bg-white/10" data-testid={`search-result-${v.id}`}>
              <Link to={`/watch/${v.id}`} state={{ sourceQueue: shown }} onClick={onClose}
                className="flex flex-1 min-w-0 items-center gap-3 pl-2 py-2 min-h-[64px]">
                <img src={v.cover_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 bg-zinc-800" />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 font-display font-semibold">
                    <span className="truncate"><Highlight text={v.title || ""} query={needle} /></span>
                    {v.premium && <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-label="Premium" />}
                  </span>
                  <span className="block text-sm text-zinc-400 truncate">
                    <Highlight text={v.artist || ""} query={needle} />
                    {note && <span className="text-cyan-300"> · {note}</span>}
                  </span>
                </span>
              </Link>
              <button type="button" aria-label={`Play ${v.title}`} onClick={() => play(i)} data-testid={`search-play-${v.id}`}
                className="w-12 h-12 mr-1 shrink-0 rounded-full bg-cyan-400 text-black flex items-center justify-center active:scale-95 transition-transform">
                <Play className="w-5 h-5 ml-0.5" />
              </button>
            </li>
          ))}
          {entries.length > 0 && (
            <li>
              <Link to={`/search?q=${encodeURIComponent(searched)}`} onClick={onClose} data-testid="search-see-all"
                className="flex items-center justify-center gap-2 min-h-[48px] rounded-xl text-sm font-medium text-cyan-300 hover:bg-white/5 active:bg-white/10">
                See all results for “{searched}” <ArrowRight className="w-4 h-4" />
              </Link>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

export default function SearchPopup({ open, onClose }) {
  // Only mounted while open, so every opening starts fresh and the search box
  // gets keyboard focus right when the person taps the search button.
  if (!open) return null;
  return <Sheet onClose={onClose} />;
}
